import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function api(endpoint, token, options = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_URL}${endpoint}`, { headers, ...options })
    .then(r => r.json())
    .catch(() => ({ success: false }));
}

const STATUS_MAP = {
  pending: { hi: 'DNS लंबित', en: 'DNS Pending', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', dot: 'bg-yellow-500' },
  verified: { hi: 'सत्यापित', en: 'Verified', color: 'bg-blue-100 text-blue-700 border-blue-300', dot: 'bg-blue-500' },
  active: { hi: 'सक्रिय', en: 'Active', color: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500' },
  failed: { hi: 'विफल', en: 'Failed', color: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
};

const SSL_MAP = {
  pending: { hi: 'लंबित', en: 'Pending', color: 'text-yellow-600' },
  provisioning: { hi: 'प्रावधान हो रहा', en: 'Provisioning', color: 'text-blue-600' },
  active: { hi: 'सक्रिय', en: 'Active', color: 'text-green-600' },
};

export default function DomainSettingsPage({ token }) {
  const { language } = useLanguage();
  const t = (hi, en) => language === 'hi' ? hi : en;
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [cnameTarget, setCnameTarget] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [verifying, setVerifying] = useState(null);

  const load = useCallback(() => {
    api('/api/domains/', token).then(d => {
      if (d.success) {
        setDomains(d.domains);
        setCnameTarget(d.cname_target);
      }
      setLoading(false);
    });
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const showMsg = (text, type = 'info') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const handleAdd = () => {
    if (!newDomain.trim()) return;
    api('/api/domains/', token, { method: 'POST', body: JSON.stringify({ domain: newDomain }) })
      .then(d => {
        if (d.success) {
          load();
          setNewDomain('');
          setShowAdd(false);
          showMsg(t('डोमेन जोड़ा गया! DNS कॉन्फ़िगर करें।', 'Domain added! Configure DNS records.'), 'success');
        } else {
          showMsg(d.detail || t('डोमेन जोड़ने में त्रुटि', 'Error adding domain'), 'error');
        }
      });
  };

  const handleVerify = (domainId) => {
    setVerifying(domainId);
    api(`/api/domains/${domainId}/verify`, token, { method: 'POST' })
      .then(d => {
        setVerifying(null);
        if (d.success) {
          showMsg(t('डोमेन सत्यापित और SSL सक्रिय!', 'Domain verified and SSL activated!'), 'success');
          load();
        } else {
          showMsg(d.message || t('DNS रिकॉर्ड नहीं मिले', 'DNS records not found'), 'error');
        }
      });
  };

  const handleDelete = (domainId) => {
    if (!window.confirm(t('क्या आप यह डोमेन हटाना चाहते हैं?', 'Remove this domain?'))) return;
    api(`/api/domains/${domainId}`, token, { method: 'DELETE' })
      .then(d => {
        if (d.success) {
          showMsg(t('डोमेन हटाया गया', 'Domain removed'), 'success');
          load();
        }
      });
  };

  const handleToggle = (domainId, field, value) => {
    api(`/api/domains/${domainId}`, token, {
      method: 'PUT',
      body: JSON.stringify({ [field]: value })
    }).then(() => load());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40" data-testid="domain-loader">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="domain-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" data-testid="domain-heading">
            {t('🌐 कस्टम डोमेन मैपिंग', '🌐 Custom Domain Mapping')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('अपने स्कूल का डोमेन शिक्षा मित्र से जोड़ें', 'Connect your school domain to Shiksha Mitra')}
          </p>
        </div>
        <button
          data-testid="add-domain-btn"
          onClick={() => setShowAdd(!showAdd)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${showAdd ? 'bg-gray-200 text-gray-700' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
        >
          {showAdd ? t('✕ बंद करें', '✕ Cancel') : t('+ डोमेन जोड़ें', '+ Add Domain')}
        </button>
      </div>

      {/* Message */}
      {msg.text && (
        <div className={`p-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 text-green-700' : msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`} data-testid="domain-message">
          {msg.text}
        </div>
      )}

      {/* Add Domain Form */}
      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="add-domain-form">
          <h3 className="font-semibold text-gray-800">{t('नया डोमेन जोड़ें', 'Add New Domain')}</h3>
          <p className="text-sm text-gray-500">{t('अपना डोमेन नाम दर्ज करें (जैसे: school.example.com)', 'Enter your domain name (e.g., school.example.com)')}</p>
          <div className="flex gap-3">
            <input
              data-testid="domain-input"
              placeholder="school.example.com"
              value={newDomain}
              onChange={e => setNewDomain(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              className="flex-1 px-4 py-2.5 border rounded-lg text-sm font-mono"
            />
            <button
              data-testid="save-domain-btn"
              onClick={handleAdd}
              disabled={!newDomain.trim()}
              className="px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition"
            >
              {t('जोड़ें', 'Add')}
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
            <p className="font-semibold mb-1">{t('ध्यान दें:', 'Note:')}</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>{t('अधिकतम 3 डोमेन प्रति स्कूल', 'Maximum 3 domains per school')}</li>
              <li>{t('डोमेन आपके पास पहले से रजिस्टर्ड होना चाहिए', 'Domain must already be registered with a registrar')}</li>
              <li>{t('DNS कॉन्फ़िगरेशन में 24-48 घंटे लग सकते हैं', 'DNS changes may take 24-48 hours to propagate')}</li>
            </ul>
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-5" data-testid="how-it-works">
        <h3 className="font-bold text-gray-800 mb-3">{t('🔧 कैसे काम करता है?', '🔧 How does it work?')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
            <div>
              <div className="font-semibold text-sm text-gray-800">{t('डोमेन जोड़ें', 'Add Domain')}</div>
              <div className="text-xs text-gray-500">{t('अपना डोमेन नाम यहाँ दर्ज करें', 'Enter your domain name here')}</div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
            <div>
              <div className="font-semibold text-sm text-gray-800">{t('DNS कॉन्फ़िगर करें', 'Configure DNS')}</div>
              <div className="text-xs text-gray-500">{t('CNAME और TXT रिकॉर्ड अपने DNS प्रदाता में जोड़ें', 'Add CNAME and TXT records at your DNS provider')}</div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
            <div>
              <div className="font-semibold text-sm text-gray-800">{t('सत्यापित करें', 'Verify')}</div>
              <div className="text-xs text-gray-500">{t('सत्यापित करें बटन दबाएं, SSL अपने-आप सक्रिय होगा', 'Click verify, SSL will be auto-activated')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Shortcut */}
      <div className="bg-white rounded-xl border p-4 flex items-center justify-between" data-testid="branding-shortcut">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-lg">🎨</div>
          <div>
            <h4 className="font-semibold text-gray-800 text-sm">{t('स्कूल ब्रांडिंग', 'School Branding')}</h4>
            <p className="text-xs text-gray-500">{t('अपने डोमेन पर logo, रंग और theme कस्टमाइज़ करें', 'Customize logo, colors & theme on your domain')}</p>
          </div>
        </div>
        <button
          data-testid="go-to-branding-btn"
          onClick={() => navigate('/settings')}
          className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition flex items-center gap-1.5"
        >
          {t('ब्रांडिंग सेटिंग्स →', 'Branding Settings →')}
        </button>
      </div>

      {/* Domains List */}
      {domains.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="domain-empty">
          <div className="text-5xl mb-3">🌐</div>
          <p className="text-lg">{t('अभी कोई कस्टम डोमेन नहीं', 'No custom domains yet')}</p>
          <p className="text-sm mt-1">{t('"+ डोमेन जोड़ें" बटन दबाकर शुरू करें', 'Click "+ Add Domain" to get started')}</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="domains-list">
          {domains.map(dom => {
            const st = STATUS_MAP[dom.status] || STATUS_MAP.pending;
            const ssl = SSL_MAP[dom.ssl_status] || SSL_MAP.pending;
            return (
              <div key={dom.domain_id} className="bg-white rounded-xl border p-5" data-testid={`domain-card-${dom.domain_id}`}>
                {/* Domain Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${st.dot} animate-pulse`} />
                    <h3 className="font-bold text-lg text-gray-800 font-mono">{dom.domain}</h3>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full border ${st.color}`}>
                      {t(st.hi, st.en)}
                    </span>
                  </div>
                  <button
                    data-testid={`delete-domain-${dom.domain_id}`}
                    onClick={() => handleDelete(dom.domain_id)}
                    className="text-red-400 hover:text-red-600 text-sm transition"
                  >
                    {t('हटाएं', 'Remove')}
                  </button>
                </div>

                {/* DNS Instructions (show when pending) */}
                {dom.status === 'pending' && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4" data-testid={`dns-instructions-${dom.domain_id}`}>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('DNS रिकॉर्ड कॉन्फ़िगर करें:', 'Configure DNS Records:')}</h4>
                    
                    {/* CNAME Record */}
                    <div className="bg-white rounded-lg border p-3 mb-2">
                      <div className="text-xs text-gray-500 mb-1 font-semibold">CNAME Record</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-xs text-gray-400">Type</span>
                          <div className="font-mono font-semibold">CNAME</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Host</span>
                          <div className="font-mono font-semibold truncate">{dom.domain}</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Target</span>
                          <div className="font-mono font-semibold text-orange-600">{cnameTarget}</div>
                        </div>
                      </div>
                    </div>

                    {/* TXT Record */}
                    <div className="bg-white rounded-lg border p-3 mb-3">
                      <div className="text-xs text-gray-500 mb-1 font-semibold">TXT Record ({t('सत्यापन', 'Verification')})</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-xs text-gray-400">Host</span>
                          <div className="font-mono font-semibold truncate">{dom.domain}</div>
                        </div>
                        <div>
                          <span className="text-xs text-gray-400">Value</span>
                          <div className="font-mono text-xs break-all text-blue-600">{dom.txt_record}</div>
                        </div>
                      </div>
                    </div>

                    <button
                      data-testid={`verify-domain-${dom.domain_id}`}
                      onClick={() => handleVerify(dom.domain_id)}
                      disabled={verifying === dom.domain_id}
                      className="w-full py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
                    >
                      {verifying === dom.domain_id ? (
                        <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> {t('जांच हो रही है...', 'Checking...')}</>
                      ) : (
                        <>{t('🔍 DNS सत्यापित करें', '🔍 Verify DNS')}</>
                      )}
                    </button>
                    {dom.check_attempts > 0 && (
                      <p className="text-xs text-gray-400 mt-2 text-center">
                        {t(`${dom.check_attempts} बार जांचा गया`, `Checked ${dom.check_attempts} time(s)`)}
                        {dom.last_check && ` | ${t('अंतिम:', 'Last:')} ${new Date(dom.last_check).toLocaleString('hi-IN')}`}
                      </p>
                    )}
                  </div>
                )}

                {/* Active Domain Info */}
                {dom.status === 'active' && (
                  <div className="space-y-3" data-testid={`active-info-${dom.domain_id}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="text-xs text-green-600">{t('SSL प्रमाणपत्र', 'SSL Certificate')}</div>
                        <div className={`font-semibold text-sm mt-0.5 ${ssl.color}`}>{t(ssl.hi, ssl.en)}</div>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-xs text-blue-600">{t('DNS स्थिति', 'DNS Status')}</div>
                        <div className="font-semibold text-sm text-green-600 mt-0.5">{t('जुड़ा हुआ', 'Connected')}</div>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <div className="text-xs text-purple-600">{t('WWW Redirect', 'WWW Redirect')}</div>
                        <div className="font-semibold text-sm mt-0.5">{dom.redirect_www ? t('चालू', 'On') : t('बंद', 'Off')}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs text-gray-500">{t('सत्यापित', 'Verified')}</div>
                        <div className="font-semibold text-sm mt-0.5">{dom.verified_at ? new Date(dom.verified_at).toLocaleDateString('hi-IN') : '-'}</div>
                      </div>
                    </div>

                    {/* Toggles */}
                    <div className="flex gap-3">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dom.ssl_enabled}
                          onChange={e => handleToggle(dom.domain_id, 'ssl_enabled', e.target.checked)}
                          className="w-4 h-4 text-orange-500 rounded"
                          data-testid={`toggle-ssl-${dom.domain_id}`}
                        />
                        <span>{t('HTTPS चालू', 'Enable HTTPS')}</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={dom.redirect_www}
                          onChange={e => handleToggle(dom.domain_id, 'redirect_www', e.target.checked)}
                          className="w-4 h-4 text-orange-500 rounded"
                          data-testid={`toggle-www-${dom.domain_id}`}
                        />
                        <span>{t('www → non-www redirect', 'www → non-www redirect')}</span>
                      </label>
                    </div>

                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700 flex items-center gap-2">
                        <span className="text-lg">&#10003;</span>
                        {t(`आपका स्कूल अब https://${dom.domain} पर उपलब्ध है`, `Your school is now live at https://${dom.domain}`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Verified but SSL pending */}
                {dom.status === 'verified' && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200" data-testid={`ssl-pending-${dom.domain_id}`}>
                    <p className="text-sm text-blue-700 flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                      {t('SSL प्रमाणपत्र प्रावधान हो रहा है... कुछ मिनट प्रतीक्षा करें', 'SSL certificate provisioning... Please wait a few minutes')}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
